<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="tid"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Configure SSO</h1>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div v-if="!statusSucceed && organizationId" class="container">
            <div class="custom-control custom-radio custom-control-inline">
                <input type="radio" id="onlyPasswords" v-model="authMode" name="authMode" class="custom-control-input" value="onlyPasswords">
                <label class="custom-control-label" for="onlyPasswords">Passwords only</label>
            </div>
            <div class="custom-control custom-radio custom-control-inline">
                <input type="radio" id="mixed" v-model="authMode" name="authMode" class="custom-control-input" value="mixed">
                <label class="custom-control-label" for="mixed">Mixed (passwords and SSO)</label>
            </div>
            <div class="custom-control custom-radio custom-control-inline">
                <input type="radio" id="onlySso" v-model="authMode" name="authMode" class="custom-control-input" value="onlySso">
                <label class="custom-control-label" for="onlySso">SSO only</label>
            </div>

            <div v-show="ssoSettingsVisible">
                <hr />

                <div class="alert alert-warning" role="alert">
                    Only OAuth2 based SSO is supported.
                </div>

                <div v-if="signInUrl" class="alert alert-primary" role="alert">
                    Users can follow this link to sign in with your SSO provider:<br />
                    <a :href="signInUrl" target="_blank">{{signInUrl}}</a>
                </div>

                <div class="form-group">
                    <label>OAuth2 client ID:</label>
                    <input v-model="clientId" type="text" class="form-control">
                </div>

                <div class="form-group">
                    <label>OAuth2 client secret:</label>
                    <input v-model="clientSecret" type="password" class="form-control">
                </div>

                <div class="form-group">
                    <label>OAuth2 token endpoint uri:</label>
                    <input v-model="tokenEndpointUri" type="text" class="form-control">
                </div>

                <div class="form-group">
                    <label>OAuth2 authorization endpoint uri:</label>
                    <input v-model="authorizationEndpointUri" type="text" class="form-control">
                </div>

                <button v-if="!statusInvoking" @click="invokeConfigureOauth2CommandV1" type="button" class="btn btn-primary btn-lg">Save</button>
                <button v-else type="button" class="btn btn-primary btn-lg" disabled>Saving...</button>
            </div>
        </div>

        <div v-else-if="statusSucceed" class="container">
            <h4>Congratulation! OAuth2 based SSO was configured.</h4>
            <a class="btn btn-primary btn-lg" :href="`/#/admin/organizations/dashboard/${tid}`" role="button">Go to dashboard</a>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, watch, onMounted } from 'vue'
    import { useRoute } from 'vue-router'

    import AdminNavigationV2 from '@common/AdminNavigationV2.vue'

    import { useSsoConfigurationQueryV1 } from '@Organizations/OrganizationUnits/queries-vue-composables'
    import { useConfigureOauth2CommandV1 } from '@Organizations/OrganizationUnits/commands-vue-composables'

    // ---------------- route ----------------

    const route = useRoute()
    const tid = computed(() => Number(route.params.tid))

    // ---------------- local state ----------------

    const authMode = ref<'onlyPasswords' | 'mixed' | 'onlySso'>('onlyPasswords')

    const clientId = ref('')
    const clientSecret = ref('')
    const tokenEndpointUri = ref('')
    const authorizationEndpointUri = ref('')

    // ---------------- query ----------------

    const {
        modelSsoConfigurationQueryV1: ssoConfiguration,
        runSsoConfigurationQueryV1
    } = useSsoConfigurationQueryV1(tid)

    // ---------------- command ----------------

    const {
        invokeConfigureOauth2CommandV1,
        invokingConfigureOauth2CommandV1,
        succeedConfigureOauth2CommandV1,
        validationErrorsConfigureOauth2CommandV1
    } = useConfigureOauth2CommandV1(
        computed(() => ssoConfiguration.value?.Id),
        clientId,
        clientSecret,
        tokenEndpointUri,
        authorizationEndpointUri
    )

    // ---------------- lifecycle ----------------

    onMounted(() =>
    {
        document.title = 'Configure SSO'
        runSsoConfigurationQueryV1()
    })

    // ---------------- sync read model → form ----------------

    watch(ssoConfiguration, (cfg) =>
    {
        if (!cfg) return

        clientId.value = cfg.ClientId
        clientSecret.value = cfg.ClientSecret
        tokenEndpointUri.value = cfg.TokenEndpointUri
        authorizationEndpointUri.value = cfg.AuthorizationEndpointUri
    }, { immediate: true })

    // ---------------- derived ----------------

    const organizationId = computed(() => ssoConfiguration.value?.Id)
    const signInUrl = computed(() => ssoConfiguration.value?.SignInUrl)
    const ssoSettingsVisible = computed(() => authMode.value === 'mixed' || authMode.value === 'onlySso')
    const statusInvoking = computed(() => invokingConfigureOauth2CommandV1.value)
    const statusSucceed = computed(() => succeedConfigureOauth2CommandV1.value)
    const validationErrors = computed(() => validationErrorsConfigureOauth2CommandV1.value)
</script>
